// PocketBase client, service URLs, the sync-status bus, and every PB
// read/write helper. UI code never talks to PocketBase directly.
import PocketBase from "pocketbase";
import { monthKey } from "./calc";

// When opened on this PC we talk to the local services; when opened via
// ottracker.ca (Cloudflare Tunnel) we talk to the public subdomains.
const IS_LOCAL = ["localhost", "127.0.0.1"].includes(window.location.hostname);
export const PB_URL      = IS_LOCAL ? "http://127.0.0.1:8090" : "https://pb.ottracker.ca";
export const SERVICE_URL = IS_LOCAL ? "http://localhost:5000" : "https://export.ottracker.ca";
export const EXPORT_URL     = SERVICE_URL + "/export";
export const ADD_USER_URL   = SERVICE_URL + "/admin/add-user";
export const DEACTIVATE_URL = SERVICE_URL + "/admin/deactivate";

export const pb = new PocketBase(PB_URL);

// ── Sync status bus (module-level, decoupled from React so it can't loop renders) ──
// PB helpers call syncStart()/syncEnd() around each network write or load; the app
// header subscribes via onSync() to render a glanceable synced/syncing/offline chip.
// storage.js reports quota failures here too, so "local cache full" is visible
// instead of a silently-swallowed exception.
let _syncPending = 0;
let _syncLast = null;
let _storageWarn = false;
let _lastError = null; // diagnostic only — human-readable text from the last failed PB write
let _syncListeners = [];
function _syncEmit() {
  const snap = { pending: _syncPending, last: _syncLast, storageWarn: _storageWarn, lastError: _lastError };
  _syncListeners.forEach(fn => { try { fn(snap); } catch {} });
}
export function syncStart() { _syncPending++; _syncEmit(); }
export function syncEnd(ok) { _syncPending = Math.max(0, _syncPending - 1); if (ok) _syncLast = Date.now(); _syncEmit(); }
export function reportStorageWarn() { if (!_storageWarn) { _storageWarn = true; _syncEmit(); } }
// Surfaces the actual PB write failure (status + message) so it's visible on
// a phone with no devtools access, instead of only console.warn.
export function reportSyncError(e) {
  const status = e && e.status;
  const msg = (e && e.response && e.response.message) || (e && e.message) || String(e);
  // PocketBase's top-level message ("Failed to create record.") is a generic
  // wrapper — the actual per-field reason lives in response.data.
  const fieldData = e && e.response && e.response.data;
  const fieldMsg = fieldData && Object.keys(fieldData).length
    ? " — " + Object.entries(fieldData).map(([k, v]) => `${k}: ${(v && v.message) || v}`).join("; ")
    : "";
  _lastError = (status ? `[${status}] ` : "") + msg + fieldMsg;
  _syncEmit();
}
export function syncPending() { return _syncPending; }
export function onSync(fn) {
  _syncListeners.push(fn);
  try { fn({ pending: _syncPending, last: _syncLast, storageWarn: _storageWarn, lastError: _lastError }); } catch {}
  return () => { _syncListeners = _syncListeners.filter(f => f !== fn); };
}

// ── Entries ──
export async function pbSaveEntry(entry, date, periodStart, userId) {
  syncStart(); let result = null, ok = false;
  try {
    // Never send the local id / pbId to PocketBase — it has its own 15-char ids,
    // and passing the local timestamp id makes create() fail validation.
    const { id, pbId, ...fields } = entry;
    const data = { ...fields, date, periodStart, user: userId };
    if (pbId) { await pb.collection("entries").update(pbId, data); result = pbId; }
    else { const rec = await pb.collection("entries").create(data); result = rec.id; }
    ok = true;
  } catch (e) { console.warn("PB save failed:", e); }
  finally { syncEnd(ok); }
  return result;
}

export async function pbDeleteEntry(pbId) {
  syncStart(); let ok = false;
  try { await pb.collection("entries").delete(pbId); ok = true; }
  catch (e) { console.warn("PB delete failed:", e); }
  finally { syncEnd(ok); }
  return ok;
}

export async function pbLoadEntries(userId) {
  syncStart(); let result = null, ok = false;
  try {
    // Only this user's entries. The server-side list rule (@request.auth.id = user)
    // also enforces this, so other users' data can never be returned.
    const records = await pb.collection("entries").getFullList({
      filter: `user = "${userId}"`,
      sort: "date",
    });
    const byDate = {};
    records.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = [];
      const { id, date, periodStart: ps, user, created, updated, collectionId, collectionName, ...rest } = r;
      byDate[r.date].push({ ...rest, pbId: id, id: id });
    });
    result = byDate; ok = true;
  } catch (e) { console.warn("PB load failed:", e); }
  finally { syncEnd(ok); }
  return result;
}

// ── Employee info ──
export async function pbSaveEmployee(empData, userId) {
  syncStart(); let ok = false;
  try {
    const existing = await pb.collection("employee_info").getFirstListItem(`user = "${userId}"`).catch(() => null);
    if (existing) await pb.collection("employee_info").update(existing.id, { ...empData, user: userId });
    else await pb.collection("employee_info").create({ ...empData, user: userId });
    ok = true;
  } catch (e) { console.warn("PB employee save failed:", e); }
  finally { syncEnd(ok); }
}

export async function pbLoadEmployee(userId) {
  syncStart(); let result = null, ok = false;
  try {
    const rec = await pb.collection("employee_info").getFirstListItem(`user = "${userId}"`);
    result = { name: rec.name || "", employeeId: rec.employeeId || "", department: rec.department || "" };
    ok = true;
  } catch (e) { /* no record yet is normal */ }
  finally { syncEnd(ok); }
  return result;
}

// ── Day notes (one plain-text note per calendar day) ──
export async function pbLoadDayNotes(userId) {
  syncStart(); let result = null, ok = false;
  try {
    const records = await pb.collection("day_notes").getFullList({ filter: `user = "${userId}"` });
    const map = {};
    records.forEach(r => { map[r.date] = { pbId: r.id, note: r.note }; });
    result = map; ok = true;
  } catch (e) { console.warn("PB notes load failed:", e); }
  finally { syncEnd(ok); }
  return result;
}
export async function pbSaveDayNote(date, note, pbId, userId) {
  syncStart(); let result = null, ok = false;
  try {
    if (pbId) { await pb.collection("day_notes").update(pbId, { note }); result = pbId; }
    else { const rec = await pb.collection("day_notes").create({ user: userId, date, note }); result = rec.id; }
    ok = true;
  } catch (e) { console.warn("PB note save failed:", e); }
  finally { syncEnd(ok); }
  return result;
}
export async function pbDeleteDayNote(pbId) {
  syncStart(); let ok = false;
  try { await pb.collection("day_notes").delete(pbId); ok = true; }
  catch (e) { console.warn("PB note delete failed:", e); }
  finally { syncEnd(ok); }
  return ok;
}

// ── Expenses ──
export async function pbLoadExpenses(userId) {
  syncStart(); let result = null, ok = false;
  try {
    const records = await pb.collection("expenses").getFullList({ filter: `user = "${userId}"`, sort: "date" });
    const byDate = {};
    records.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = [];
      byDate[r.date].push({ id: r.id, pbId: r.id, type: r.type, amount: r.amount, project: r.project || "", reason: r.reason || "", location: r.location || "", receipt: r.receipt || "", date: r.date, costCentre: r.costCentre || "62" });
    });
    result = byDate; ok = true;
  } catch (e) { console.warn("PB expenses load failed:", e); }
  finally { syncEnd(ok); }
  return result;
}
export async function pbSaveExpense(exp, userId) {
  syncStart(); let result = null, ok = false;
  try {
    const data = { user: userId, date: exp.date, month: monthKey(exp.date), type: exp.type, amount: Number(exp.amount) || 0, project: exp.project || "", reason: exp.reason || "", location: exp.location || "", receipt: exp.receipt || "", costCentre: exp.costCentre || "62" };
    // The local cache may have dropped the receipt image to fit the storage
    // quota (receiptOmitted). Never let that cached gap wipe the synced copy.
    if (exp.receiptOmitted && !exp.receipt) delete data.receipt;
    if (exp.pbId) { await pb.collection("expenses").update(exp.pbId, data); result = exp.pbId; }
    else { const rec = await pb.collection("expenses").create(data); result = rec.id; }
    ok = true;
  } catch (e) { console.warn("PB expense save failed:", e); reportSyncError(e); }
  finally { syncEnd(ok); }
  return result;
}
export async function pbDeleteExpense(pbId) {
  syncStart(); let ok = false;
  try { await pb.collection("expenses").delete(pbId); ok = true; }
  catch (e) { console.warn("PB expense delete failed:", e); }
  finally { syncEnd(ok); }
  return ok;
}

// ── Time-off balance anchors (one record per account: hours as of a date) ──
export async function pbLoadTimeAccounts(userId) {
  syncStart(); let result = null, ok = false;
  try {
    const records = await pb.collection("time_accounts").getFullList({ filter: `user = "${userId}"` });
    const map = {};
    records.forEach(r => { map[r.account] = { pbId: r.id, hours: r.hours, asOf: r.asOf }; });
    result = map; ok = true;
  } catch (e) { console.warn("PB time accounts load failed:", e); }
  finally { syncEnd(ok); }
  return result;
}
export async function pbSaveTimeAccount(account, rec, userId) {
  syncStart(); let result = null, ok = false;
  try {
    const data = { user: userId, account, hours: Number(rec.hours) || 0, asOf: rec.asOf };
    if (rec.pbId) { await pb.collection("time_accounts").update(rec.pbId, data); result = rec.pbId; }
    else { const r = await pb.collection("time_accounts").create(data); result = r.id; }
    ok = true;
  } catch (e) { console.warn("PB time account save failed:", e); }
  finally { syncEnd(ok); }
  return result;
}
